import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import { IAuthConfig } from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    LanguageService,
    SettingService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import {
    Observable,
    of,
} from 'rxjs';

interface ITimezone {
    id: string;
    text: string;
}

interface ILanguage {
    id: string;
    text: string;
    icon?: {
        iconOnly?: boolean;
        svgIcon?: string;
        matIcon?: string;
    };
}

@Component({
    selector: 'ui-general-settings',
    templateUrl: './ui-general-settings.component.html',
    styleUrls: [
        '../ui-setting-form.scss',
        './ui-general-settings.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiGeneralSettingsComponent extends UiSettingForm implements OnInit, OnChanges {
    @Input()
    public showConnectionString?: boolean;
    public connectionString$?: Observable<string>;
    public timezones?: ITimezone[];
    public languages?: ILanguage[];
    public authConfig$: Observable<IAuthConfig>;

    constructor(
        public settingService: SettingService,
        protected _cd: ChangeDetectorRef,
        private _fb: FormBuilder,
        private _translateService: TranslateService,
        private _languageService: LanguageService,
        private _snack: UiSnackBarService,
        appState: AppStateFacade,
    ) {
        super(
            _cd,
            appState,
        );

        this.authConfig$ = appState.authConfig.value$;
    }

    ngOnInit() {
        if (this.showConnectionString) {
            this.connectionString$ = this.settingService.getConnectionString();
        }
        this.formGroup = this._fb.group({
            'timezone': [[]],
            'language': [[]],
            'isModernFolderSystemEnabled': [this.settings.general.isModernFolderSystemEnabled],
        });
    }

    async ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.settings) {
            this.timezones =
                this.timezones ||
                (await this.settingService.getTimezones().toPromise()).map(tz => {
                    return {
                        id: tz.Value,
                        text: tz.Name,
                    };
                });

            this.formGroup.get('timezone')!.patchValue(
                (this.timezones || []).filter(tz => tz.id === this.settings.general.timezone),
            );

            this.languages =
                this.languages ||
                (await this._languageService.getAll().toPromise()).map(lang => {
                    return {
                        id: lang.Name,
                        text: lang.DisplayName,
                        icon: {
                            iconOnly: lang.Name === 'ja',
                            svgIcon: lang.Name === 'ja' ? 'uipath:language_ja' : undefined,
                        },
                    };
                });

            this.formGroup.get('language')!.patchValue(
                (this.languages || []).filter(lang => lang.id === this.settings.general.language),
            );

            this._cd.markForCheck();
        }

        if (changes.showConnectionString != null) {
            this.connectionString$ = changes.showConnectionString.currentValue ?
                this.settingService.getConnectionString() :
                of('');
            this._cd.markForCheck();
        }
    }

    public onClipboardCopy() {
        this._snack.success(this._translateService.instant('settings_general_form_connection_string_copy_message'));
    }
}
